
import React from 'react';
import { Moon, Users, Smile, Play, Instagram, Youtube } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import FaitImage from './FaitImage';
import FadeIn from './FadeIn';

const Gallery: React.FC = () => {
  const { t } = useLanguage();

  const VIDEOS = [
    {
      type: 'youtube',
      src: 'https://www.youtube.com/embed/0XMcpPZLMPk',
      title: 'סקירה מלאה',
      colSpan: 'col-span-1 md:col-span-3',
      aspect: 'aspect-video'
    },
    {
      type: 'youtube',
      src: 'https://www.youtube.com/embed/J0cGbl_ayo8',
      title: 'YouTube Shorts',
      colSpan: 'col-span-1',
      aspect: 'aspect-[9/16]'
    },
    {
      type: 'tiktok',
      src: 'https://www.tiktok.com/embed/v2/7445038717040938248',
      title: 'TikTok',
      colSpan: 'col-span-1',
      aspect: 'aspect-[9/16]'
    },
    {
      type: 'instagram',
      src: 'https://www.instagram.com/reel/DTkuk-jCILt/embed/',
      title: 'Instagram',
      colSpan: 'col-span-1',
      aspect: 'aspect-[9/16]'
    }
  ];

  return (
    <section id="gallery" className="py-24 bg-[#15100d] relative overflow-hidden">
      
      {/* Decorative gradient */}
      <div className="absolute top-0 inset-x-0 h-40 bg-gradient-to-b from-[#120C08] to-transparent z-0"></div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <div className="text-center mb-16">
          <span className="text-fait-gold font-sans text-sm tracking-widest uppercase mb-2 block">{t.gallery.subtitle}</span>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-fait-gold drop-shadow-md">{t.gallery.title}</h2>
          <p className="mt-4 text-gray-400 max-w-lg mx-auto font-sans text-lg">
             {t.gallery.description}
          </p>
        </div>

        {/* Featured Image - Using 'quiet-corner.jpg' */}
        <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-16 group h-[400px]">
             <FaitImage 
                src="/images/quiet-corner.jpg" 
                alt="Atmosphere" 
                className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700"
                imageContext="crowd"
             />
             {/* Strong dark overlay for readability */}
             <div className="absolute inset-0 bg-black/60 group-hover:bg-black/50 transition-colors duration-500"></div>
             
             <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center z-10">
                 <h3 className="text-3xl font-serif font-bold text-white mb-4 drop-shadow-lg border-b-2 border-fait-gold/50 pb-2">
                     {t.gallery.splitTitleHighlight}
                 </h3>
                 <p className="text-gray-100 max-w-lg font-sans leading-relaxed drop-shadow-lg text-lg font-medium">
                     {t.gallery.splitText}
                 </p>
             </div>
        </div>

        {/* Atmosphere Features with Icons */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
            <div className="bg-[#1E1611] p-6 rounded-xl border border-white/5 text-center hover:bg-[#251b15] transition-colors">
                <div className="flex justify-center mb-4">
                    <Moon className="text-fait-gold w-10 h-10" />
                </div>
                <h4 className="text-xl font-bold text-fait-cream mb-2 font-serif">{t.gallery.features[0].title}</h4>
                <p className="text-gray-500 font-sans text-sm">{t.gallery.features[0].text}</p>
            </div>

            <div className="bg-[#1E1611] p-6 rounded-xl border border-white/5 text-center hover:bg-[#251b15] transition-colors">
                 <div className="flex justify-center mb-4">
                    <Users className="text-fait-gold w-10 h-10" />
                </div>
                <h4 className="text-xl font-bold text-fait-cream mb-2 font-serif">{t.gallery.features[1].title}</h4>
                <p className="text-gray-500 font-sans text-sm">{t.gallery.features[1].text}</p>
            </div>

            <div className="bg-[#1E1611] p-6 rounded-xl border border-white/5 text-center hover:bg-[#251b15] transition-colors">
                 <div className="flex justify-center mb-4">
                    <Smile className="text-fait-gold w-10 h-10" />
                </div>
                <h4 className="text-xl font-bold text-fait-cream mb-2 font-serif">{t.gallery.features[2].title}</h4>
                <p className="text-gray-500 font-sans text-sm">{t.gallery.features[2].text}</p>
            </div>
        </div>

        {/* --- VIDEO SECTION --- */}
        <div className="mb-24">
            <div className="text-center mb-12">
               <FadeIn direction="up">
                  <div className="inline-flex items-center justify-center p-3 bg-fait-gold/10 rounded-full mb-4">
                      <Play className="text-fait-gold w-8 h-8 fill-current" />
                  </div>
                  <h3 className="text-3xl md:text-4xl font-serif font-bold text-white mb-2">{t.gallery.videoTitle}</h3>
                  <p className="text-gray-400 font-sans">{t.gallery.videoSubtitle}</p>
               </FadeIn>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {VIDEOS.map((video, idx) => (
                    <FadeIn key={idx} delay={idx * 100} className={`relative group ${video.colSpan}`}>
                        <div className={`w-full ${video.aspect} rounded-2xl overflow-hidden shadow-2xl border border-fait-gold/20 bg-black relative`}>
                            <iframe 
                                src={video.src} 
                                title={video.title}
                                className="w-full h-full object-cover"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                allowFullScreen
                                loading="lazy"
                            ></iframe>
                        </div>
                        {/* Decorative Badge */}
                        <div className="absolute top-4 right-4 bg-fait-dark/80 backdrop-blur text-fait-gold text-xs font-bold px-3 py-1 rounded-full border border-fait-gold/30 opacity-0 group-hover:opacity-100 transition-opacity">
                            {video.title}
                        </div>
                    </FadeIn>
                ))}
            </div>
        </div>

      </div>
    </section>
  );
};

export default Gallery;
